import { Component, OnInit, ChangeDetectorRef, AfterViewInit, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { PageLayoutComponent } from '../../shared/page-layout/page-layout';

import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ApiGroupDisplay, PolicyBaseline, ApiGroupsService, ApiGroupResponse, ApiGroupRequest, PolicyDropdownItem } from '../../../service/api-groups/api-groups.service';
import { forkJoin, of } from 'rxjs';
import { finalize, catchError } from 'rxjs/operators';
import { StateContainerComponent, ViewState } from '../../shared/state-container/state-container.component';

const API_GROUP_TARGET_TYPE = 'api group';

@Component({
  selector: 'app-api-groups',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule, 
    PageLayoutComponent, 
    Button, 
    DialogModule, 
    InputText, 
    Select, 
    Tooltip,
    ToastModule,
    StateContainerComponent
  ],
  providers: [MessageService],
  templateUrl: `./api-groups.component.html`,
})
export class ApiGroupsComponent implements OnInit, AfterViewInit {
  apiGroups: ApiGroupDisplay[] = [];
  policyBaselines: PolicyBaseline[] = [];
  policyDropdownItems: PolicyDropdownItem[] = [];
  
  isLoading = signal(false);
  isLoadingPolicies = false;
  
  // Error state
  error = signal<any>(null);

  policyOptions: { label: string; value: number }[] = [];

  // Policy to scopes mapping
  policyScopeMapping: Map<number, string[]> = new Map();

  // Attribute options for dynamic rule builder
  attributeOptions = [
    { label: 'Business domain', value: 'Business domain' },
    { label: 'Backend system', value: 'Backend system' },
    { label: 'Environment', value: 'Environment' },
    { label: 'API tag', value: 'API tag' },
    { label: 'Service name', value: 'Service name' }
  ];

  operatorOptions = [
    { label: 'Equals', value: 'Equals' },
    { label: 'Does not equal', value: 'Does not equal' },
    { label: 'Contains', value: 'Contains' },
    { label: 'Is one of', value: 'Is one of' }
  ];

  valueOptions = [
    { label: 'Dealer commerce', value: 'Dealer commerce' },
    { label: 'SAP B1', value: 'SAP B1' },
    { label: 'Bank collections', value: 'Bank collections' },
    { label: 'Production', value: 'Production' },
    { label: 'UAT', value: 'UAT' },
    { label: 'Identity core', value: 'Identity core' }
  ];

  membershipOptions = [
    { label: 'Dynamic', value: 'Dynamic' },
    { label: 'Static', value: 'Static' },
    { label: 'Synced', value: 'Synced' }
  ];

  membershipEvaluationOptions = [
    { label: 'Real-time on change', value: 'Real-time on change' },
    { label: 'Every 15 minutes', value: 'Every 15 minutes' },
    { label: 'Daily reconciliation', value: 'Daily reconciliation' }
  ];

  rateLimitOptions = ['500', '1000', '2000', '10000', 'Unlimited'];
  timeoutOptions = ['5', '15', '30', '60'];
  pathHandlingOptions = ['Preserve path', 'Strip route prefix', 'Rewrite path'];

  selectedMembershipEvaluation = 'Real-time on change';

  showModal = false;
  editingIndex: number | null = null;
  editForm: ApiGroupDisplay = {
    name: '',
    type: 'Dynamic',
    members: '0 routes',
    rule: '',
    scope: [],
    policy: '',
    rateLimit: '1000',
    timeout: '15',
    pathHandling: 'Preserve path'
  };

  // Rule builder fields
  selectedAttribute = 'Business domain';
  selectedOperator = 'Equals';
  selectedValue = 'Dealer commerce';
  showRuleBuilder = true;

  // Computed state for StateContainer
  readonly viewState = computed<ViewState>(() => {
    if (this.isLoading()) return 'loading';
    if (this.error()) return 'error';
    if (this.apiGroups.length === 0) return 'empty';
    return 'content';
  });

  readonly emptyStateMessage = computed(() => {
    return 'Create your first API group to organize routes by business domain, backend system, platform or environment.';
  });

  readonly errorMessage = computed(() => {
    const err = this.error();
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    return 'Failed to load API groups. Please try again.';
  });

  constructor(
    private apiGroupsService: ApiGroupsService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.isLoading.set(true);
    this.cdr.detectChanges();
    
    // Load both API groups and policies in parallel
    this.loadData();
  }

  ngAfterViewInit() {
    // Additional change detection after view initialization
    this.cdr.detectChanges();
  }

  loadData() {
    // Load API groups and policies in parallel
    forkJoin({
      groups: this.apiGroupsService.getApiGroups().pipe(
        catchError(error => {
          console.error('Error loading groups:', error);
          this.error.set(error);
          return of([]);
        })
      ),
      policies: this.apiGroupsService.getPoliciesDropdown().pipe(
        catchError(error => {
          console.error('Error loading policies:', error);
          return of([]);
        })
      )
    }).pipe(
      finalize(() => {
        this.isLoading.set(false);
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: (result) => {
        console.log('API Groups received:', result.groups);
        console.log('Policies received:', result.policies);
        
        // Process policies first
        this.processPolicies(result.policies);
        
        // Process groups
        this.apiGroups = result.groups.map(group => this.mapApiGroupToDisplay(group));
        
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Failed to load data', error);
        this.error.set(error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load data. Please try again.'
        });
        this.cdr.detectChanges();
      }
    });
  }

  retryLoad() {
    this.isLoading.set(true);
    this.error.set(null);
    this.loadData();
  }

  getVisibleScopes(scopes: string[]): string[] {
    if (!scopes || scopes.length === 0) {
      return [];
    }
    
    const MAX_VISIBLE = 3;
    return scopes.slice(0, MAX_VISIBLE);
  }

  /**
   * Get count of remaining scopes
   */
  getRemainingScopesCount(scopes: string[]): number {
    if (!scopes || scopes.length === 0) {
      return 0;
    }
    
    const MAX_VISIBLE = 3;
    return Math.max(0, scopes.length - MAX_VISIBLE);
  }

  /**
   * Get tooltip text for remaining scopes
   */
  getRemainingScopesTooltip(scopes: string[]): string {
    if (!scopes || scopes.length === 0) {
      return '';
    }
    
    const MAX_VISIBLE = 3;
    const remaining = scopes.slice(MAX_VISIBLE);
    return remaining.join(', ');
  }

  private filterApiGroupPolicies(policies: PolicyDropdownItem[]): PolicyDropdownItem[] {
    return (policies || []).filter(
      p => (p.targetType || '').trim().toLowerCase() === API_GROUP_TARGET_TYPE
    );
  }
  
  processPolicies(policies: PolicyDropdownItem[]) {
    const filtered = this.filterApiGroupPolicies(policies);

    this.policyDropdownItems = filtered;
    this.policyOptions = filtered.map(p => ({
      label: p.name,
      value: p.id
    }));

    this.policyScopeMapping.clear();
    filtered.forEach(p => {
      this.policyScopeMapping.set(p.id, p.scopes || []);
    });
  }

  loadPolicies() {
    this.isLoadingPolicies = true;
    this.apiGroupsService.getPoliciesDropdown()
      .pipe(
        finalize(() => {
          this.isLoadingPolicies = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (policies: PolicyDropdownItem[]) => {
          const filtered = this.filterApiGroupPolicies(policies);

          this.policyDropdownItems = filtered;
          this.policyOptions = filtered.map(p => ({
            label: p.name,
            value: p.id
          }));

          this.policyScopeMapping.clear();
          filtered.forEach(p => {
            this.policyScopeMapping.set(p.id, p.scopes || []);
          });
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Failed to load policies', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load policies'
          });
          this.cdr.detectChanges();
        }
      });
  }

  // Map API group response to display model with proper null handling
  mapApiGroupToDisplay(group: ApiGroupResponse): ApiGroupDisplay {
    // Determine type based on code
    let type: ApiGroupDisplay['type'] = 'Dynamic';
    
    if (group.code) {
      const codeLower = group.code.toLowerCase();
      if (codeLower.includes('static')) type = 'Static';
      else if (codeLower.includes('synced')) type = 'Synced';
      else if (codeLower.includes('platform')) type = 'Platform';
      else if (codeLower.includes('backend') || codeLower.includes('sap')) type = 'Backend system';
      else if (codeLower.includes('domain') || codeLower.includes('commerce') || codeLower.includes('collection')) type = 'Business domain';
    }
    
    // Handle null values - check if policyBaseline exists
    const policyName = group.policyBaseline?.name || 'No policy assigned';
    const scopes = group.policyBaseline?.scopes || [];
    const routeCount = group.routeCount || 0;
    
    // Build rule from code or use default
    let rule = 'Dynamic rule';
    if (group.code) {
      rule = `code = ${group.code}`;
    }
    
    return {
      id: group.id,
      name: group.name || 'Unnamed Group',
      code: group.code,
      type: type,
      members: `${routeCount} route${routeCount !== 1 ? 's' : ''}`,
      rule: rule,
      scope: scopes,
      policy: policyName,
      policyId: group.policyBaseline?.id,
      rateLimit: group.rateLimit || 'Unlimited',
      timeout: group.upstreamTimeout || '30',
      pathHandling: group.pathHandling || 'Preserve path',
      routeCount: routeCount,
      policyBaseline: group.policyBaseline || undefined
    };
  }

  getTypeClasses(type: string): string {
    const map: Record<string, string> = {
      Dynamic: 'bg-blue-100 text-blue-700',
      Static: 'bg-emerald-100 text-emerald-700',
      Synced: 'bg-amber-100 text-amber-700',
      'Business domain': 'bg-emerald-100 text-emerald-700',
      'Backend system': 'bg-blue-100 text-blue-700',
      Platform: 'bg-violet-100 text-violet-700',
    };

    return map[type] ?? 'bg-slate-100 text-slate-600';
  }

  getScopesForPolicy(policyId: number): string[] {
    if (!policyId) return [];
    return this.policyScopeMapping.get(policyId) || [];
  }

  onPolicyChange() {
    // Find the policy by ID from the selected policy value
    const selectedPolicy = this.policyOptions.find(p => p.value === this.editForm.policyId);
    if (selectedPolicy) {
      const scopes = this.getScopesForPolicy(selectedPolicy.value);
      this.editForm.scope = [...scopes];
      this.editForm.policy = selectedPolicy.label;
    }
  }

  toggleRuleBuilder() {
    this.showRuleBuilder = this.editForm.type === 'Dynamic';
  }

  buildRule(): string {
    if (this.editForm.type === 'Dynamic') {
      return `${this.selectedAttribute} ${this.selectedOperator} ${this.selectedValue}`;
    } else if (this.editForm.type === 'Static') {
      return 'Manual membership';
    } else {
      return 'External source mapping';
    }
  }

  openCreateGroup() {
    this.editingIndex = null;
    this.editForm = {
      name: '',
      type: 'Dynamic',
      members: '0 routes',
      rule: '',
      scope: [],
      policy: '',
      rateLimit: '1000',
      timeout: '15',
      pathHandling: 'Preserve path'
    };
    this.selectedAttribute = 'Business domain';
    this.selectedOperator = 'Equals';
    this.selectedValue = 'Dealer commerce';
    this.showRuleBuilder = true;
    this.showModal = true;
    
    // Load policies when dialog opens
    if (this.policyOptions.length === 0) {
      this.loadPolicies();
    }
  }

  configureGroup(group: ApiGroupDisplay, index: number) {
    this.editingIndex = index;
    this.editForm = { ...group };
    
    // Parse rule if it's dynamic
    if (group.type === 'Dynamic' && group.rule) {
      const parts = group.rule.split(' ');
      if (parts.length >= 3) {
        this.selectedAttribute = parts[0];
        this.selectedOperator = parts[1];
        this.selectedValue = parts.slice(2).join(' ');
      }
    }
    
    this.showRuleBuilder = group.type === 'Dynamic';
    this.showModal = true;
    
    // Load policies when dialog opens
    if (this.policyOptions.length === 0) {
      this.loadPolicies();
    }
  }

  viewRoutes(group: ApiGroupDisplay) {
    // Navigate to routes view or open modal
    this.messageService.add({
      severity: 'info',
      summary: 'Info',
      detail: `Viewing ${group.members} for: ${group.name}`
    });
  }

  deleteGroup(index: number) {
    const group = this.apiGroups[index];
    if (group.id && confirm(`Are you sure you want to delete "${group.name}"?`)) {
      // For now, just remove from local list
      this.apiGroups.splice(index, 1);
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: `API group "${group.name}" deleted`
      });
    } else if (!group.id) {
      // If no ID, just remove from local list
      this.apiGroups.splice(index, 1);
    }
  }

  closeModal() {
    this.showModal = false;
  }

  saveGroup() {
    if (!this.editForm.name.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Group name is required'
      });
      return;
    }

    if (!this.editForm.policyId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select a policy baseline'
      });
      return;
    }

    // Build the request payload
    const payload: ApiGroupRequest = {
      name: this.editForm.name,
      policyId: this.editForm.policyId,
      rateLimit: this.editForm.rateLimit || '1000',
      upstreamTimeout: this.editForm.timeout || '15',
      pathHandling: this.editForm.pathHandling || 'Preserve path'
    };

    if (this.editingIndex === null) {
      // Create new group
      this.apiGroupsService.createApiGroup(payload).subscribe({
        next: (createdGroup: ApiGroupResponse) => {
          this.apiGroups.push(this.mapApiGroupToDisplay(createdGroup));
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `API group "${createdGroup.name}" created`
          });
          this.closeModal();
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Failed to create API group', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create API group'
          });
        }
      });
    } else {
      // Update existing group
      const groupId = this.apiGroups[this.editingIndex].id;
      if (groupId) {
        this.apiGroupsService.updateApiGroup(groupId, payload).subscribe({
          next: (updatedGroup: ApiGroupResponse) => {
            this.apiGroups[this.editingIndex!] = this.mapApiGroupToDisplay(updatedGroup);
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `API group "${updatedGroup.name}" updated`
            });
            this.closeModal();
            this.cdr.detectChanges();
          },
          error: (error) => {
            console.error('Failed to update API group', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update API group'
            });
          }
        });
      } else {
        // Fallback for local updates
        const groupToSave = {
          ...this.editForm,
          rule: this.buildRule()
        };
        this.apiGroups[this.editingIndex] = groupToSave;
        this.closeModal();
        this.cdr.detectChanges();
      }
    }
  }

  getScopeDisplay(scopes: string[]): string {
    if (!scopes || scopes.length === 0) {
      return 'No scopes assigned';
    }
    return scopes.join(', ');
  }
}